import "../../components/ProfileCard/ProfileCard.scss";
import "./AboutAccount.scss";
import {useEffect, useState} from "react";
import {useLocation, useHistory} from "react-router-dom";
import axios from "axios";
import {useUserStore} from "../../../../../modules/shop/user/store";
import {copyText} from "../../../../shared/functions/clickCopyText";
import {getCurrentDateInSeconds} from "../../../../shared/functions/getRandomId";
import Toaster from "../../../../shared/Toaster";
import {ToasterProps} from "../../../../shared/Toaster/Toaster";
import {PATHS_API} from "../../../../../enum";
import {SpinButton, withMinSpin} from "../../components/ProfileCard/ProfileCard";

// що показати гравцю після повернення з Discord (?discord=...)
const DISCORD_RESULT: Record<string, { message: string, type: ToasterProps["type"] }> = {
    linked: { message: "Discord прив'язано", type: "success" },
    denied: { message: "Прив'язку скасовано", type: "info" },
    taken: { message: "Цей Discord уже прив'язаний до іншого акаунта", type: "error" },
    expired: { message: "Посилання застаріло — спробуйте ще раз", type: "warning" },
    login: { message: "Увійдіть на сайт і спробуйте ще раз", type: "warning" },
    not_configured: { message: "Прив'язка Discord поки недоступна", type: "warning" },
    failed: { message: "Не вдалося прив'язати Discord, спробуйте пізніше", type: "error" },
};

const DiscordIcon = ({ size = 22 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M19.3 5.4A16.5 16.5 0 0015.2 4l-.5 1a15 15 0 00-5.4 0l-.5-1a16.5 16.5 0 00-4.1 1.4C2 9.3 1.3 13.1 1.7 16.8a16.6 16.6 0 005 2.6l1.1-1.7c-.6-.2-1.2-.5-1.7-.8l.4-.3a11.8 11.8 0 0011 0l.4.3c-.5.3-1.1.6-1.7.8l1.1 1.7a16.6 16.6 0 005-2.6c.5-4.3-.8-8-3-11.4zM8.7 14.5c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2zm6.6 0c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2z" />
    </svg>
);

// Обліковий запис: Steam-профіль із SteamID для копіювання та прив'язка Discord
const AboutAccount = () => {
    const [toastProps, setToastProps] = useState<ToasterProps | null>(null);
    const [unlinking, setUnlinking] = useState(false);
    const user = useUserStore(store => store.user)
    const fetchUser = useUserStore(store => store.fetchUser)
    const location = useLocation();
    const history = useHistory();

    const toast = (message: string, type: ToasterProps["type"]) => setToastProps({ message, type, id: getCurrentDateInSeconds() + message });

    // результат прив'язки з адреси показуємо один раз і прибираємо параметр
    useEffect(() => {
        const result = new URLSearchParams(location.search).get("discord");
        if (!result) return;
        const info = DISCORD_RESULT[result] ?? DISCORD_RESULT.failed;
        toast(info.message, info.type);
        if (result === "linked") fetchUser();
        history.replace({ pathname: location.pathname });
    }, []);

    const copySteamId = async () => {
        try {
            await copyText(user.steamId)
            toast("SteamID скопійовано", "success")
        } catch {
            toast("Не вдалося скопіювати SteamID", "error")
        }
    }

    const unlink = async () => {
        if (!window.confirm("Відв'язати Discord від акаунта?")) return;
        setUnlinking(true);
        try {
            await withMinSpin(() => axios.delete(PATHS_API.DISCORD, { withCredentials: true }));
            await fetchUser();
            toast("Discord відв'язано", "success");
        } catch {
            toast("Не вдалося відв'язати Discord", "error");
        } finally {
            setUnlinking(false);
        }
    }

    const discordLinked = !!user?.discordId;

    return (
        <div className="bc-page">
            <div className="bc-card acc-card">
                { user?.avatarUrl
                    ? <img className="acc-avatar" src={user.avatarUrl} alt="" />
                    : <span className="acc-avatar acc-avatar-empty">{user?.personaName?.[0]?.toUpperCase() ?? "?"}</span>
                }
                <h3 className="bc-title">{user?.personaName}</h3>
                <p className="bc-text">Акаунт Steam, через який ви входите на сайт</p>

                <button type="button" className="acc-steamid" onClick={copySteamId} title="Скопіювати SteamID">
                    <span className="acc-steamid-label">SteamID</span>
                    <span className="acc-steamid-value">{user?.steamId}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="9" y="9" width="12" height="12" rx="2" />
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                </button>
            </div>

            <div className={`acc-discord ${discordLinked ? "is-linked" : ""}`}>
                <div className="acc-discord-logo"><DiscordIcon /></div>
                { discordLinked
                    ? <div className="acc-discord-info">
                        <b>Discord прив'язано</b>
                        <span className="acc-discord-user">
                            { user.discordAvatarUrl && <img src={user.discordAvatarUrl} alt="" /> }
                            {user.discordUsername}
                        </span>
                    </div>
                    : <div className="acc-discord-info">
                        <b>Прив'яжіть Discord</b>
                        <span>Так ми знатимемо, хто ви в нашому Discord. Ми бачимо лише ваше ім'я та аватар — без доступу до повідомлень.</span>
                    </div>
                }
                { discordLinked
                    ? <SpinButton type="button" loading={unlinking} onClick={unlink} className="acc-unlink">Відв'язати</SpinButton>
                    : <a className="bc-discord" href={`${PATHS_API.DISCORD}/link`}>Прив'язати</a>
                }
            </div>

            {toastProps && <Toaster message={toastProps.message} type={toastProps.type} id={toastProps.id} />}
        </div>
    );
};

export default AboutAccount;
